import { useState, useEffect } from 'react';
import { useEditor } from '../../context/EditorContext';
import {
    RiEditLine,
    RiLayoutMasonryLine,
    RiHistoryLine,
    RiDownloadLine,
    RiArrowGoBackLine,
    RiArrowGoForwardLine,
    RiEyeLine,
    RiEyeOffLine,
    RiDeleteBinLine,
    RiDragMoveLine
} from 'react-icons/ri';
import { Reorder, useDragControls } from 'framer-motion';
import type { NormalizedResume, Section } from '../../models/resume-draft';

type Tab = 'edit' | 'structure' | 'versions' | 'export';

export default function EditorSidePanel() {
    const [activeTab, setActiveTab] = useState<Tab>('structure');
    const { undo, redo, canUndo, canRedo } = useEditor();

    return (
        <div className="flex flex-col h-full text-white bg-[#0c0e14] border-l border-white/5">
            {/* Toolbar */}
            <div className="flex items-center justify-between p-4 border-b border-white/5">
                <div className="flex gap-2">
                    <button
                        onClick={undo}
                        disabled={!canUndo}
                        className="p-2 rounded hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent text-gray-400 hover:text-white transition-colors"
                        title="Undo"
                    >
                        <RiArrowGoBackLine />
                    </button>
                    <button
                        onClick={redo}
                        disabled={!canRedo}
                        className="p-2 rounded hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent text-gray-400 hover:text-white transition-colors"
                        title="Redo"
                    >
                        <RiArrowGoForwardLine />
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-white/5">
                <button
                    onClick={() => setActiveTab('edit')}
                    className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'edit' ? 'border-blue-500 text-white' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
                >
                    <div className="flex flex-col items-center gap-1">
                        <RiEditLine size={18} />
                        <span>Edit</span>
                    </div>
                </button>
                <button
                    onClick={() => setActiveTab('structure')}
                    className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'structure' ? 'border-blue-500 text-white' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
                >
                    <div className="flex flex-col items-center gap-1">
                        <RiLayoutMasonryLine size={18} />
                        <span>Structure</span>
                    </div>
                </button>
                <button
                    onClick={() => setActiveTab('versions')}
                    className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'versions' ? 'border-blue-500 text-white' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
                >
                    <div className="flex flex-col items-center gap-1">
                        <RiHistoryLine size={18} />
                        <span>History</span>
                    </div>
                </button>
                <button
                    onClick={() => setActiveTab('export')}
                    className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'export' ? 'border-blue-500 text-white' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
                >
                    <div className="flex flex-col items-center gap-1">
                        <RiDownloadLine size={18} />
                        <span>Export</span>
                    </div>
                </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                {activeTab === 'edit' && <EditTab />}
                {activeTab === 'structure' && <StructureTab />}
                {activeTab === 'versions' && <VersionsTab />}
                {activeTab === 'export' && <ExportTab />}
            </div>
        </div>
    );
}

// --- Subcomponents ---

function EditTab() {
    return (
        <div className="space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400">AI Assistant</h3>
            <p className="text-sm text-gray-500">Select text in the editor to see improvement suggestions.</p>

            <div className="p-4 rounded-lg bg-white/5 border border-white/5">
                <p className="text-xs text-blue-400 font-medium mb-2">Example Tools</p>
                <div className="flex flex-col gap-2">
                    <button className="text-left text-sm px-3 py-2 rounded bg-white/5 hover:bg-white/10 transition-colors">
                        ✨ Improve clarity
                    </button>
                    <button className="text-left text-sm px-3 py-2 rounded bg-white/5 hover:bg-white/10 transition-colors">
                        📉 Shorten text
                    </button>
                    <button className="text-left text-sm px-3 py-2 rounded bg-white/5 hover:bg-white/10 transition-colors">
                        🎯 Tailor to job description
                    </button>
                </div>
            </div>
        </div>
    );
}

function StructureTab() {
    const { currentVersion, updateResume } = useEditor();
    // We need local state for the drag-and-drop to be smooth
    const [localSections, setLocalSections] = useState<Section[]>([]);

    const sections = currentVersion?.resume_json?.sections || [];

    // Initialize/Sync local state when external sections change (e.g. undo/redo)
    // We only want to sync if the *content* or *external order* changed, but we also want to avoid
    // overwriting the drag state if we are dragging.
    // For simplicity, we sync whenever `sections` prop changes, assuming updates are fast enough OR user isn't dragging while an external update happens.
    // To properly separate Header vs Body, we need to handle them.

    useEffect(() => {
        if (sections.length > 0) {
            const sorted = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));
            setLocalSections(sorted);
        }
    }, [sections]);

    if (!currentVersion || !currentVersion.resume_json) {
        return <div className="text-gray-500 text-sm">Loading structure...</div>;
    }

    const commitReorder = (reorderedBody: Section[]) => {
        // 1. Construct full list
        // Header should always be order 0 (or stay where it was if we just sort around it)
        const header = localSections.find(s => s.content_type === 'key_value_pairs');

        // Remove header from reorderedBody just in case, though it shouldn't be there if we separated
        const cleanBody = reorderedBody.filter(s => s.content_type !== 'key_value_pairs');

        const finalSections = [];
        let orderCounter = 0;

        if (header) {
            finalSections.push({ ...header, order: orderCounter++ });
        }

        cleanBody.forEach(s => {
            finalSections.push({ ...s, order: orderCounter++ });
        });

        // 2. Deep copy and update
        const updatedResume: NormalizedResume = JSON.parse(JSON.stringify(currentVersion.resume_json));
        updatedResume.sections = finalSections;

        updateResume(updatedResume, 'reorder');
        setLocalSections(finalSections); // Sync local state carefully
    };

    const toggleVisibility = (sectionId: string) => {
        const updatedResume: NormalizedResume = JSON.parse(JSON.stringify(currentVersion.resume_json));
        const section = updatedResume.sections?.find(s => s.section_id === sectionId);

        if (section) {
            section.visible = section.visible === undefined ? false : !section.visible;
            updatedResume.sections = (updatedResume.sections || []).map(s =>
                s.section_id === sectionId ? section : s
            );
            updateResume(updatedResume, 'visibility_change');
        }
    };

    const deleteSection = (sectionId: string) => {
        if (!window.confirm("Are you sure you want to delete this section?")) return;
        const updatedResume: NormalizedResume = JSON.parse(JSON.stringify(currentVersion.resume_json));
        updatedResume.sections = (updatedResume.sections || []).filter(s => s.section_id !== sectionId);
        updateResume(updatedResume, 'manual_edit');
    };

    // Separate Header and Body for Reorder
    const headerSection = localSections.find(s => s.content_type === 'key_value_pairs');
    const bodySections = localSections.filter(s => s.content_type !== 'key_value_pairs');

    return (
        <div className="space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400">Reorder Sections</h3>
            <p className="text-sm text-gray-500">Drag items to reorder or toggle visibility.</p>

            <div className="flex flex-col gap-2">
                {/* Header (Pinned) */}
                {headerSection && (
                    <div className="flex items-center justify-between p-3 rounded bg-white/5 border border-white/5 opacity-80">
                        <div className="flex items-center gap-3">
                            <div className="flex flex-col gap-0.5 text-gray-600 px-1 cursor-not-allowed">
                                <RiDragMoveLine size={16} />
                            </div>
                            <span className="font-medium text-sm text-gray-200">
                                {headerSection.title || 'Header'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Header actions often restricted */}
                        </div>
                    </div>
                )}

                {/* Draggable Body */}
                <Reorder.Group axis="y" values={bodySections} onReorder={(newOrder) => commitReorder(newOrder)} className="flex flex-col gap-2">
                    {bodySections.map((section) => (
                        <SortableSectionItem
                            key={section.section_id}
                            section={section}
                            onToggleVisibility={() => toggleVisibility(section.section_id)}
                            onDelete={() => deleteSection(section.section_id)}
                        />
                    ))}
                </Reorder.Group>
            </div>

            {localSections.length === 0 && (
                <div className="text-center p-4 text-gray-500 border border-dashed border-white/10 rounded">
                    No sections found.
                </div>
            )}
        </div>
    );
}

interface SortableSectionItemProps {
    section: Section;
    onToggleVisibility: () => void;
    onDelete: () => void;
}

function SortableSectionItem({ section, onToggleVisibility, onDelete }: SortableSectionItemProps) {
    const controls = useDragControls();

    return (
        <Reorder.Item
            value={section}
            dragListener={false}
            dragControls={controls}
            className={`
                flex items-center justify-between p-3 rounded bg-white/5 border border-white/5 
                hover:bg-white/10 transition-colors group select-none
                ${!section.visible && section.visible !== undefined ? 'opacity-50' : ''}
            `}
        >
            <div className="flex items-center gap-3">
                <div
                    onPointerDown={(e) => controls.start(e)}
                    className="flex items-center justify-center text-gray-500 hover:text-white cursor-grab active:cursor-grabbing p-1 -ml-1 rounded hover:bg-white/10 transition-colors"
                >
                    <RiDragMoveLine size={18} />
                </div>
                <span className="font-medium text-sm text-gray-200">
                    {section.section_id || 'Untitled Section'}
                </span>
            </div>

            <div className="flex items-center gap-2">
                <button
                    onClick={onToggleVisibility}
                    className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                    title={section.visible === false ? "Show" : "Hide"}
                >
                    {section.visible === false ? <RiEyeOffLine size={16} /> : <RiEyeLine size={16} />}
                </button>

                <button
                    onClick={onDelete}
                    className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-white/10 transition-colors"
                    title="Delete"
                >
                    <RiDeleteBinLine size={16} />
                </button>
            </div>
        </Reorder.Item>
    );
}

function VersionsTab() {
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400">Version History</h3>
                <span className="text-xs text-gray-500">Auto-saved</span>
            </div>
            <div className="relative pl-4 border-l border-white/10 space-y-6">
                <div className="relative">
                    <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-green-500 ring-4 ring-[#0c0e14]" />
                    <p className="text-sm font-medium text-white">Current Version</p>
                    <p className="text-xs text-gray-500">Just now • Manual Edit</p>
                </div>
                <div className="relative">
                    <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-gray-600 ring-4 ring-[#0c0e14]" />
                    <p className="text-sm font-medium text-gray-400">Version 1</p>
                    <p className="text-xs text-gray-500">2 mins ago • AI Generated</p>
                </div>
            </div>
        </div>
    );
}

import { useReactToPrint } from 'react-to-print';

function ExportTab() {
    const { resumeId, canvasRef, resumeName } = useEditor();

    const handleDownload = useReactToPrint({
        contentRef: canvasRef,
        documentTitle: resumeName || 'Resume',
        onAfterPrint: () => console.log('Print success'),
    });

    return (
        <div className="space-y-6">
            <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20">
                <h3 className="text-lg font-bold text-white mb-2">Ready to download?</h3>
                <p className="text-sm text-gray-400 mb-4">
                    Finalize your resume to generate a PDF. This will lock the current version.
                </p>
                <button
                    onClick={() => handleDownload && handleDownload()}
                    className="w-full py-2.5 rounded-lg bg-white text-black font-bold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
                >
                    <RiDownloadLine />
                    Download PDF
                </button>
            </div>

            <div className="text-xs text-gray-500 p-4 border border-white/5 rounded-lg">
                <p className="font-medium text-gray-400 mb-1">Note:</p>
                <p>Downloading will remove all draft versions and keep only this final verified copy.</p>
            </div>
        </div>
    );
}
